import React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'

import Mousetrap from 'components/mousetrap/Mousetrap'
import Draggable from 'components/draggable/Draggable'
import Button from 'components/button/Button'
import Hamburger from 'assets/icons/hamburger.svg'
import Plus from 'assets/icons/plus.svg'
import { IProps as ITableCellEditorParams } from './TableCellEditor'

import createError from 'utils/createError'

import styles from './stringListInput.scss'

interface IProps extends ITableCellEditorParams {
  initialValue: string[] | null
}

interface IState {
  value: string[] | null
  newItem: string | null
  draggedOverIdx: number
}

class StringListInput extends React.PureComponent<IProps, IState> {
  private items: Array<React.RefObject<HTMLInputElement>> = []
  private draggedItem: React.RefObject<HTMLInputElement> | null = null

  constructor(props: IProps) {
    super(props)

    const { initialValue } = props

    if (!Array.isArray(initialValue)) {
      throw createError('StringListInput.constructor', 'Invalid initialValue', {
        initialValue,
      })
    }

    this.state = {
      value: initialValue,
      newItem: null,
      draggedOverIdx: -1,
    }

    this.items = Array.from({
      length: Math.max(initialValue.length, 1),
    }).map(() => React.createRef()) // If list is initially empty, we'll render an "empty" item, so account for that

    this.items.push(React.createRef()) // And one more for the `newItem` input
  }

  // Called by ag-grid to get the final value when editing is finished
  getValue = () => this.state.value

  focus = () => {
    const { value } = this.state

    if (!value) return

    this.items[Math.max(value.length, 0)].current?.focus() // Focus the last item
  }

  // Handle a change in the main input
  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const parsedValue = JSON.parse(e.currentTarget.value)
      if (Array.isArray(parsedValue)) {
        this.setState({ value: parsedValue.map(v => String(v)) })
      }
    } catch (e) {
      // Do not propagate changes if the value is not a valid array
    }
  }

  // Handle a change in one of the items
  handleChangeItem = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = this.state

    const changedItem = String(e.currentTarget.value)

    if (!Array.isArray(value)) {
      throw createError('StringListInput.handleChangeItem', 'Invalid value', {
        value,
        idx,
        changedItem,
      })
    }

    const newValue = [...value]
    newValue.splice(idx, 1, changedItem)
    return this.setState({
      value: newValue,
    })
  }

  handleChangeNewItem = (e: React.ChangeEvent<HTMLInputElement>) => {
    const changedItem = String(e.currentTarget.value)

    this.setState({
      newItem: changedItem,
    })
  }

  handleAddNewItem = () => {
    const { value, newItem } = this.state

    if (!Array.isArray(value)) {
      throw createError('StringListInput.handleAddItem', 'Invalid value', {
        value,
        newItem,
      })
    }

    // Create a ref for this new item
    this.items.push(React.createRef())
    this.setState({ value: [...value, newItem || ''], newItem: null }, () => {
      // Focus the `newItem` input
      this.items[this.items.length - 1].current?.focus()
      // And scroll so that the newly created element is visible
      this.items[this.items.length - 2].current?.scrollIntoView(false)
    })
  }

  // Runs when you press Enter on an existing item
  handleEnterKeydown = (idx: number) => {
    const { value } = this.state

    if (!Array.isArray(value)) {
      throw createError('StringListInput.handleEnterKeydown', 'Invalid value', {
        value,
        idx,
      })
    }

    // Create a new value next to the current element
    let newValue = [...value]
    newValue.splice(idx + 1, 0, '')

    // Create a new ref for the new item
    this.items.push(React.createRef())

    this.setState(
      {
        value: newValue,
        newItem: null,
      },
      () => {
        // Focus and scroll to the newly created item
        this.items[idx + 1].current?.focus()
        this.items[idx + 1].current?.scrollIntoView(false)
        this.items[idx + 1].current?.scrollIntoView({ block: 'nearest' }) // This is only supported on Chromium, and is more relevant. On Firefox, this is a no-op so the previous `scrollIntoView` would apply
      }
    )
  }

  handleRemoveItem = (idx: number, e: React.MouseEvent<HTMLButtonElement>) => {
    const { value } = this.state
    e.preventDefault()
    e.stopPropagation()

    if (!Array.isArray(value)) {
      throw createError('StringListInput.handleRemoveClick', 'Invalid Value', {
        value,
        idx,
      })
    }

    const valueCopy = [...value]
    valueCopy.splice(idx, 1) // Remove the item from value state

    this.items.splice(idx, 1)
    this.items[idx - 1]?.current?.focus() // Focus previous element
    this.items[idx - 1]?.current?.scrollIntoView()

    // We put this setState in a `0` setTimeout so it executes in the next tick
    // This is necessary so the DOM element does not get removed before `handleClickOutside` in `TableCellEditor` has a chance to trace its ancestry to figure out if the "remove" button click was inside thee tooltip or not (DOMNode.contains())
    setTimeout(() => this.setState({ value: valueCopy }), 0)
  }

  handleTabKeydown = (idx: number, e: ExtendedKeyboardEvent) => {
    const { value } = this.state

    if (!Array.isArray(value)) {
      throw createError('StringListInput.handleTabKeydown', 'Invalid value', {
        value,
        idx,
      })
    }

    if (idx === value.length) {
      // If on the last visible item, do nothing and let ag-grid focus the next cell for editing
    } else {
      // Otherwise, focus the next input
      e.preventDefault()
      e.stopPropagation()

      this.items[idx + 1].current?.focus()
    }
  }

  handleShiftTabKeydown = (idx: number, e: ExtendedKeyboardEvent) => {
    if (idx === 0) {
      // If on the first item, do nothing and let ag-grid focus the previous cell for editing
    } else {
      // Otherwise, focus the previous input
      e.preventDefault()
      e.stopPropagation()
      this.items[idx - 1].current?.focus()
    }
  }

  handleFinishEditing = () => {
    this.props.stopEditing()
  }

  handleDragStart = (idx: number) => {
    this.draggedItem = this.items[idx]
  }

  handleDragOver = (idx: number, e: React.DragEvent<HTMLLIElement>) => {
    e.preventDefault()
    this.setState({
      draggedOverIdx: idx,
    })
  }

  handleDragEnd = (idx: number) => {
    if (!this.draggedItem) {
      return
    }

    const { value = [] } = this.state

    if (!Array.isArray(value)) {
      throw createError('StringListInput.HandleDragEnd', 'Invalid value', {
        value,
        idx,
      })
    }

    const valueCopy = [...value]
    const changedItem = String(this.draggedItem.current?.value) // Dragged item value

    this.items.splice(idx, 1) // Remove the item
    valueCopy.splice(idx, 1) // Remove the dragged item from value state

    if (this.state.draggedOverIdx > idx) {
      // When you reorder downwards
      this.items.splice(this.state.draggedOverIdx - 1, 0, this.draggedItem) // Insert item infront of the last passed index
      valueCopy.splice(this.state.draggedOverIdx - 1, 0, changedItem) // Insert item value
    } else {
      this.items.splice(this.state.draggedOverIdx, 0, this.draggedItem)
      valueCopy.splice(this.state.draggedOverIdx, 0, changedItem)
    }

    this.draggedItem = null
    this.setState({
      value: valueCopy,
      draggedOverIdx: -1,
    })
  }

  render() {
    const { value } = this.state

    if (!Array.isArray(value)) {
      throw createError('StringListInput.render', 'Invalid value', {
        value,
      })
    }

    return (
      <>
        <div data-cy="input--string-list" className={styles.container}>
          <input
            type="text"
            className={styles.input}
            value={JSON.stringify(value)}
            onChange={this.handleChange}
          />
          {/*
            Hate doing this, but apply the dark theme to the container so that the scrollbar isn't ugly.
            TODO:: Re-look at this after themeing is finished
          */}
          <ul className={cn(styles.itemContainer, 'ag-theme-dark')}>
            <div className={styles.itemScrollContainer}>
              {/* This separator only exists to illuminate when you drag over the first item (to indicate where you will drop) */}
              <div
                className={cn(styles.separator, {
                  [styles.draggedOver]: this.state.draggedOverIdx === 0,
                })}
              />

              {value.map((v, idx) => (
                <React.Fragment key={idx}>
                  <ListItem
                    innerRef={this.items[idx]}
                    value={v}
                    onChange={this.handleChangeItem.bind(this, idx)}
                    onRemove={this.handleRemoveItem.bind(this, idx)}
                    onEnter={this.handleEnterKeydown.bind(this, idx)}
                    onTab={this.handleTabKeydown.bind(this, idx)}
                    onShiftTab={this.handleShiftTabKeydown.bind(this, idx)}
                    onModEnter={this.handleFinishEditing.bind(this, idx)}
                    onDragStart={this.handleDragStart.bind(this, idx)}
                    onDragEnd={this.handleDragEnd.bind(this, idx)}
                    onDragOver={this.handleDragOver.bind(this, idx)}
                  />
                  <div
                    className={cn(styles.separator, {
                      [styles.draggedOver]:
                        this.state.draggedOverIdx === idx + 1,
                    })}
                  />
                </React.Fragment>
              ))}
            </div>

            <ListItem
              innerRef={this.items[value.length]} // Theres one more item than value
              value={this.state.newItem || ''}
              onChange={this.handleChangeNewItem}
              onAdd={this.handleAddNewItem}
              onEnter={this.handleAddNewItem}
              onShiftTab={this.handleShiftTabKeydown.bind(this, value.length)}
              onDragOver={this.handleDragOver.bind(this, value.length)}
            />
          </ul>
        </div>
      </>
    )
  }
}

/**
 * Single item in the list editor
 */
class ListItem extends React.Component<{
  innerRef: React.RefObject<HTMLInputElement>
  value: string
  tabIndex?: number
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onAdd?: (e: React.MouseEvent<HTMLButtonElement>) => void
  onRemove?: (e: React.MouseEvent<HTMLButtonElement>) => void
  onEnter?: (e: ExtendedKeyboardEvent) => void
  onTab?: (e: ExtendedKeyboardEvent) => void
  onShiftTab?: (e: ExtendedKeyboardEvent) => void
  onModEnter?: (e: ExtendedKeyboardEvent) => void
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void
  onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void
  onDragOver?: (e: React.DragEvent<HTMLLIElement>) => void
}> {
  render() {
    return (
      <li className={styles.item} onDragOver={this.props.onDragOver}>
        {this.props.onDragStart && this.props.onDragEnd && (
          <Draggable
            className={styles.dragButton}
            onDragStart={this.props.onDragStart}
            onDragEnd={this.props.onDragEnd}
          >
            <Hamburger />
          </Draggable>
        )}
        <input
          ref={this.props.innerRef}
          className={styles.input}
          type="text"
          value={this.props.value}
          tabIndex={this.props.tabIndex}
          onChange={this.props.onChange}
        />
        {this.props.onAdd && (
          <Button
            data-cy="input--string-list__add-btn"
            onClick={this.props.onAdd}
            className={styles.addScalarListItemBtn}
          >
            Add
          </Button>
        )}
        {this.props.onRemove && (
          <Button
            data-cy="input--string-list__remove-btn"
            onClick={this.props.onRemove}
            className={styles.closeButton}
          >
            <Plus />
          </Button>
        )}

        {this.props.onEnter && (
          <Mousetrap
            keys="enter"
            target={this.props.innerRef}
            onMatch={this.props.onEnter!}
          />
        )}
        {this.props.onTab && (
          <Mousetrap
            keys={['tab']}
            preventDefault={false}
            stopPropagation={false}
            target={this.props.innerRef}
            onMatch={this.props.onTab!}
          />
        )}
        {this.props.onShiftTab && (
          <Mousetrap
            keys={['shift+tab']}
            preventDefault={false}
            stopPropagation={false}
            target={this.props.innerRef}
            onMatch={this.props.onShiftTab}
          />
        )}
        {this.props.onModEnter && (
          <Mousetrap
            keys={['mod+enter']}
            preventDefault={false}
            stopPropagation={false}
            target={this.props.innerRef}
            onMatch={this.props.onModEnter}
          />
        )}
      </li>
    )
  }
}

export default observer(StringListInput)
